import { Disclosure, Switch } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import React, { useEffect, useState } from "react";
import { IBookingSlotsProps } from "../interface";
import ClinicLocationWhatsapp from "../ClinicLocationWhatsapp";

import {
  IBookingSlotsClinicData,
  IBookingSlotsDoctorData,
  IBookingSlotsDoctorInfo,
  IBookingSlotsResponseData,
  IBookingsSlotsData,
} from "@/api_framework/api_modals/Bookings";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import SkeletonType1 from "@/app/components/ui/Skeletons/Type1";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useUtils } from "@/app/hooks/useUtils";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { useAtHomeAvailableDoctorsAPI } from "@/app/hooks/api_hooks/bookings/useAtHomeAvailableDoctorsAPI";
import { useBookingSlotsListAPI } from "@/app/hooks/api_hooks/bookings/useBookingSlotsListAPI";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";

import {
  ISlotsState,
  setAtHomePreviousFlow,
  setAtHomeSelectedDoctorId,
  setSelectedSlotDetails,
  setSelectedSlotStaffDetails,
  setSelectedSlotsClinicDetails,
  setSlotBookingFilters,
} from "@/app_redux/reducers/slices/auth/slots_slice";
import clsx from "clsx";
import CommentSectionModal from "../CommentSection";
import { AiOutlineInfoCircle } from "react-icons/ai";
import FrontDeskOverbookingComponent from "../FrontDeskOverbookingComponent";
import AhsBookingLinkCard from "../AhsBookingLinkCard";
import { shouldShowAhsBookingLinkCard } from "../AhsBookingLinkCard/utils";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { PROCESS_STEPS_CARDS } from "../constants";
import travelIcon from "@/assets/images/travel.svg";
import { HOME_SERVICE_ID } from "@/constants";


const AvailableAndBookedSlotsComponent: React.FC<IBookingSlotsProps> = () => {
  /* Instantiations */
  const isSlotListInProgress = useLoader("isAvailableSlotsInProgress");
  const dispatch = useAppDispatch();
  const { addAMPMToTime, formatShiftTimings } = useUtils();
  const { BASIC } = useImageConstants();
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("SLOTS", "id");
  const { routeState } = useRouteInfo(routeId);
  const {
    slotsList,
    atHomeSlotBuckets,
    selectedSlotDetails,
    isPageReadonly,
    filters,
    rescheduleFlag,
    atHomePreviousFlow,
    ahsSubServiceId,
    ahsSubServiceName,
  } = routeState?.state as ISlotsState;
  const { customerId, petId, customerDetails } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState
    .state as ICustomerProfileState;
  const isHomeServiceSlot = filters?.service_type_id === HOME_SERVICE_ID;
  const { execute: logClick } = useClickLogAPI();
  const vmsUserId = useAppSelector((state) => state.auth?.user?.email);
  const { execute: fetchAvailableDoctors, doctors } = useAtHomeAvailableDoctorsAPI();
  const fetchAvailableSlots = useBookingSlotsListAPI();
  const [selectedDoctorId, setSelectedDoctorId] = useState<string | null>(null);
  const isFrontDeskOverbooking = useFeatureAccess("FRONT_DESK_OVERBOOKING") && !isPageReadonly;

  const showAhsBookingLinkCard = shouldShowAhsBookingLinkCard(routeState?.state as ISlotsState);
  const isAtHomeBucketView = isHomeServiceSlot && !rescheduleFlag && !filters?.previous_flow && !showAhsBookingLinkCard;
  const isSlotSelectionDisabled = isPageReadonly || Boolean(filters?.previous_flow);
  const isPreviousFlow = Boolean(atHomePreviousFlow);
  const showPreviousFlowToggle = isHomeServiceSlot && !rescheduleFlag;

  const handlePreviousFlowChange = (enabled: boolean) => {
    dispatch(setAtHomePreviousFlow(enabled));
    dispatch(setSelectedSlotDetails({ data: null }));
    if (!filters) return;
    const updatedFilters = { ...filters };
    if (enabled) {
      updatedFilters.previous_flow = true;
    } else {
      delete updatedFilters.previous_flow;
    }
    dispatch(setSlotBookingFilters({ filters: updatedFilters }));
    const payloadFilters =
      updatedFilters?.clinic_ids?.length > 0 ? { ...updatedFilters, city_id: null } : updatedFilters;
    fetchAvailableSlots({ ...payloadFilters, slot_status: "0" });
  };

  /* Clinic location WhatsApp share modal state */
  const [locationShareClinic, setLocationShareClinic] = useState<{ clinic_id: string; clinic_name: string } | null>(
    null,
  );

  /* APIs Hooks */
  useEffect(() => {
    setSelectedDoctorId(null);
    dispatch(setAtHomeSelectedDoctorId(null));
    if (!isAtHomeBucketView) return;
    const { latitude, longitude } = filters?.location_details ?? {};
    if (!filters?.user_id || !filters?.booking_date || !latitude || !longitude) return;
    fetchAvailableDoctors({
      customer_id: filters.user_id,
      slot_date: filters.booking_date,
      latitude,
      longitude,
    });
  }, [
    isAtHomeBucketView,
    filters?.user_id,
    filters?.booking_date,
    filters?.service_id,
    filters?.sub_service_id,
    filters?.location_details?.latitude,
    filters?.location_details?.longitude,
    fetchAvailableDoctors,
    dispatch,
  ]);

  const handleSlotSelection = (
    slot: IBookingSlotsResponseData,
    docSlot: IBookingsSlotsData,
    doc: IBookingSlotsDoctorData,
  ) => {
    logClick({
      event_type: "SLOT_SELECTED",
      scr_id: isPageReadonly ? "CHECK_SLOT" : "BOOKING_PAGE",
      client_id: customerId,
      pet_id: petId,
      vms_user_id: vmsUserId,
      clinic_id: slot?.clinic_id,
      clinic_name: slot?.clinic_name,
      staff_id: doc?.staff_id,
      cta_text: docSlot?.start_time,
      prepaid_slots: docSlot?.prepaid_booking_slot?.toLowerCase() === "yes" ? 1 : 0,
      timestamp: Date.now(),
    });
    /* Actions on when the slot is selected */
    dispatch(
      setSelectedSlotDetails({
        data: docSlot,
      }),
    );
    dispatch(
      setSelectedSlotStaffDetails({
        data: {
          experience: doc.experience,
          expertise: doc.expertise,
          staff_id: doc.staff_id,
          staff_name: doc.staff_name,
          preferred_staff: doc.preferred_staff,
          tags: doc.tags,
        },
      }),
    );
    dispatch(
      setSelectedSlotsClinicDetails({
        data: {
          clinic_id: slot.clinic_id,
          clinic_name: slot.clinic_name,
          vetbuddy_clinic_name: slot.vetbuddy_clinic_name,
        },
      }),
    );
  };

  const handleAtHomeSlotSelection = (slot: IBookingsSlotsData) => {
    logClick({
      event_type: "SLOT_SELECTED",
      scr_id: isPageReadonly ? "CHECK_SLOT" : "BOOKING_PAGE",
      client_id: customerId,
      pet_id: petId,
      vms_user_id: vmsUserId,
      clinic_id: slot?.clinic_id,
      staff_id: slot?.staff_id,
      cta_text: slot?.start_time,
      prepaid_slots: slot?.prepaid_booking_slot?.toLowerCase() === "yes" ? 1 : 0,
      timestamp: Date.now(),
    });
    dispatch(setSelectedSlotDetails({ data: slot }));
    dispatch(setSelectedSlotStaffDetails({ data: { staff_id: slot?.staff_id } as IBookingSlotsDoctorInfo }));
    dispatch(setSelectedSlotsClinicDetails({ data: { clinic_id: slot?.clinic_id } as IBookingSlotsClinicData }));
  };

  const handleDoctorClick = (doctorId: string | null) => {
    setSelectedDoctorId(doctorId);
    dispatch(setAtHomeSelectedDoctorId(doctorId));
    fetchAvailableSlots({
      ...filters,
      staff_ids: doctorId ? [doctorId] : [],
      slot_status: "0",
    });
  };

  const hasSlots = isAtHomeBucketView ? atHomeSlotBuckets?.length > 0 : slotsList?.length > 0;

  const atHomeDoctorChips = isAtHomeBucketView && doctors?.length > 0 && (
    <div className="flex flex-wrap items-start gap-2 pb-1">
      {doctors.map((doctor, doctorId) => {
        const staffId = doctor?.staff?.id ?? null;
        const chipLabel = doctor?.staff?.name ?? doctor?.tag?.tag_text;
        const chipImage = doctor?.staff?.image_url || doctor?.tag?.icon_url;
        return (
          <button
            key={staffId ?? `best-available:${doctorId}`}
            disabled={isPageReadonly}
            className={clsx(
              "flex h-10 items-stretch overflow-hidden rounded-[8px] border transition-colors",
              selectedDoctorId === staffId
                ? "border-[#0068FF] bg-[#F0F6FF] text-[#0068FF]"
                : "border-[#E2E2E2] bg-white text-[#101828] hover:border-[#0068FF]",
            )}
            onClick={isPageReadonly ? null : () => handleDoctorClick(staffId)}
          >
            {chipImage && <img src={chipImage} alt="" className="h-full w-9 shrink-0 object-cover" />}
            <span className="flex items-center px-3 text-[13px] font-medium">{chipLabel}</span>
          </button>
        );
      })}
    </div>
  );

  const howItWorksPanel = (
    <div className="w-full lg:w-[32%] self-start rounded-[10px] border border-[#FFFFFF] bg-white p-2">
      <div className="rounded-[8px] bg-[#F0F6FF] p-[12px]">
        <p className="text-[16px] font-semibold text-[#000000]">How it Works</p>
        <p className="text-[12px] text-[#6C727F]">Explain the process to pet parent through what to expect</p>
      </div>
      <div className="mt-4 space-y-4">
        {PROCESS_STEPS_CARDS.map((item, index) => (
          <div className="flex gap-3" key={item.id}>
            <div className="flex flex-col items-center">
              <div className="h-[34px] w-[34px] rounded-full bg-[#F5F4F4] flex items-center justify-center">
                <img src={item.icon} className="h-4 w-4" alt="" />
              </div>
              {index !== PROCESS_STEPS_CARDS.length - 1 && (
                <div className="w-px flex-1 mt-2 border-l border-dashed border-[#D0D5DD]" />
              )}
            </div>
            <div className="pt-0.5">
              <p className="text-[12px] font-medium text-[#000000]">{item.title}</p>
              <p className="text-[12px] text-[#6C727F] mt-1">{item.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  /* Front desk books directly as overbooking, so it never sees the slot grid */
  if (isFrontDeskOverbooking) return <FrontDeskOverbookingComponent />;

  if (showAhsBookingLinkCard) {
    return (
      <div className="mt-0 rounded-b-2xl bg-white pb-16">
        <div className="w-full pt-1 flex flex-col lg:flex-row gap-3">
          <div className="w-full lg:w-[68%] rounded-[10px] bg-[#FFFFFF] p-2">
            <AhsBookingLinkCard
              customerId={customerId}
              petId={petId}
              subServiceId={ahsSubServiceId ?? null}
              subServiceName={ahsSubServiceName ?? null}
              customerMobile={customerDetails?.mobile}
            />
          </div>
          {howItWorksPanel}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-0 rounded-b-2xl bg-white pb-16">
      <div className="mb-10">
        {!isSlotListInProgress ? (
          hasSlots ? (
            <div className={`w-full pt-1 ${isHomeServiceSlot ? "flex flex-col lg:flex-row gap-3" : ""}`}>
              <div className={`w-full relative ${isHomeServiceSlot ? "lg:w-[68%] rounded-[10px] bg-[#FFFFFF] p-2" : ""}`}>
                {isHomeServiceSlot && !isAtHomeBucketView && (
                  <div className="px-2 py-1">
                    <p className="text-[16px] font-semibold text-[#000000]">Nearest Available Clinics</p>
                    <p className="text-[12px] text-[#6C727F] mt-0.5">Doctors will be dispatched from the selected clinic</p>
                  </div>
                )}
                <div className={`w-full py-1 flex flex-col gap-2 p-2 ${showPreviousFlowToggle ? "mb-20" : "mb-12"}`}>
                  {atHomeDoctorChips}
                  {isAtHomeBucketView &&
                    atHomeSlotBuckets?.map((bucket, bucketId) => (
                      <div className="flex flex-col gap-2.5" key={`${bucket?.heading}:${bucketId}`}>
                        <p className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[#6C727F]">
                          {bucket?.heading}
                        </p>
                        <div
                          className={clsx(
                            "flex pt-0 flex-wrap items-start content-start gap-2.5",
                            isSlotSelectionDisabled && "cursor-not-allowed",
                          )}
                        >
                          {bucket?.slots?.map((slot, idx) => (
                            <button
                              disabled={isSlotSelectionDisabled}
                              className={clsx(
                                "border  rounded-md px-4 py-2 flex justify-between items-center gap-1 shadow-sm transition-all",
                                isSlotSelectionDisabled
                                  ? "cursor-not-allowed"
                                  : "hover:scale-105 hover:border hover:border-blue-500 hover:bg-blue-100 hover:text-blue-700",
                                selectedSlotDetails?.slot_id === slot.slot_id && "border-blue-500 bg-blue-200 text-blue-700",
                              )}
                              key={`${slot.start_time}:${idx}`}
                              onClick={isSlotSelectionDisabled ? null : () => handleAtHomeSlotSelection(slot)}
                            >
                              {addAMPMToTime(slot?.start_time)}{" "}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  {!isAtHomeBucketView &&
                    slotsList?.map((slot, id) => {
                    return (
                      <Disclosure key={id} defaultOpen={id === 0}>
                        {({ open }) => (
                          <>
                            <Disclosure.Button className=" px-[12px] py-[10px] items-center flex w-full justify-between rounded-[8px] bg-[#F4F4F4] text-left text-sm font-medium  focus:outline-none ">
                              <div className="flex flex-row gap-16 items-center">
                                <div className="flex gap-1 items-start">
                                  <div className="w-[400px]">
                                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                      <span className="text-[14px] font-semibold text-[#101828] leading-none">{slot?.clinic_name}</span>
                                      {slot?.clinic_id && (
                                        <button
                                          type="button"
                                          title="Share clinic location on WhatsApp"
                                          className="flex items-center justify-center leading-none text-[#E20000] hover:text-[#B80000] focus:outline-none"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            logClick({
                                              event_type: "CLINIC_LOCATION_INFO_CLICKED",
                                              scr_id: isPageReadonly ? "CHECK_SLOT" : "BOOKING_PAGE",
                                              client_id: customerId,
                                              pet_id: petId,
                                              vms_user_id: vmsUserId,
                                              clinic_id: slot?.clinic_id,
                                              clinic_name: slot?.clinic_name,
                                              timestamp: Date.now(),
                                            });
                                            setLocationShareClinic({
                                              clinic_id: slot?.clinic_id,
                                              clinic_name: slot?.clinic_name,
                                            });
                                          }}
                                        >
                                          <AiOutlineInfoCircle className="w-3 h-3" />
                                        </button>
                                      )}
                                      {(slot?.is_full_day_open || slot?.is_midnight_clinic) && (
                                        <span className="inline-flex items-center gap-1.5 bg-white px-2.5 py-1 border border-[#86EFAC] rounded-md font-semibold text-[#15803D] text-[11px] leading-none">
                                          <span className="bg-[#16A34A] rounded-full w-1.5 h-1.5 animate-pulse" />
                                          {slot?.is_full_day_open ? "24×7 Open" : "Open till Midnight"}
                                        </span>
                                      )}
                                    </div>
                                    {slot?.data?.length === 0 && (
                                      <p className="mt-1 text-[#E20000] font-medium">No Slots Available</p>
                                    )}
                                    {slot?.user_persona_message && (
                                      <p className="mt-1 text-[#d1a41a] font-medium">{slot?.user_persona_message}</p>
                                    )}
                                  </div>
                                  <span className="flex items-center h-[16px] text-[12px] text-[#6C6C6C]">
                                    {slot?.distance_from_user !== null &&
                                      slot?.distance_from_user !== undefined &&
                                      slot?.distance_from_user !== "" && (
                                        <span className="flex items-center justify-center">
                                          <img src={travelIcon} alt="travel icon" className="w-3 h-3 mr-1" />
                                          <span className="ml-1">{slot.distance_from_user} km</span>
                                        </span>
                                      )}
                                  </span>
                                </div>
                              </div>
                              {slot?.data?.length > 0 && (
                                <div className="bg-[#FFFFFF] w-[34px] h-[34px] rounded-[8px] flex items-center justify-center">
                                  <ChevronDownIcon className={`${open ? "rotate-180 transform" : ""} h-6 w-6 `} />
                                </div>
                              )}
                            </Disclosure.Button>
                            {slot?.data?.length > 0 && (
                              <Disclosure.Panel className="px-4 pb-2  text-sm text-gray-500 ">
                                {slot?.data?.map((doc, id) => {
                                  return (
                                    <div className="w-full flex items-start border-b border-solid border-gray-300 py-3" key={id}>
                                      <div className="w-1/5	 flex flex-col gap-1 ">
                                        <h1 className="text-[#0068FF] text-xs font-medium	">{doc?.staff_name}</h1>
                                        {formatShiftTimings(doc?.shift?.shift_timings) && (
                                          <span className="w-fit text-[#475467] text-[11px] font-medium">
                                            {formatShiftTimings(doc?.shift?.shift_timings)}
                                          </span>
                                        )}
                                        <p className="text-[#808DA0] text-xs font-medium	">{doc?.expertise}</p>
                                        <p className="text-[#808DA0] text-xs font-medium	">{doc?.experience}</p>
                                        {doc?.preferred_staff && (
                                          <button className="bg-green-600 text-white py-1 px-0 w-[100px] rounded-md whitespace-nowrap ">
                                            Preferred
                                          </button>
                                        )}
                                        {doc?.tags
                                          ?.slice()
                                          ?.sort((a, b) => (a?.priority ?? 0) - (b?.priority ?? 0))
                                          ?.map((tag, tagIdx) => (
                                            <button
                                              key={`${doc?.staff_id}-tag-${tagIdx}`}
                                              className="py-1 px-2 min-w-[100px] w-fit rounded-md whitespace-nowrap text-[12px]"
                                              style={{
                                                backgroundColor: tag?.tag_bg,
                                                color: tag?.tag_color,
                                              }}
                                            >
                                              {tag?.tag_text}
                                            </button>
                                          ))}
                                      </div>
                                      <div
                                        className={clsx(
                                          "flex pt-0 flex-wrap items-start content-start gap-2.5 w-4/5",
                                          doc?.data?.length > 27 && "overflow-auto h-36",
                                          isSlotSelectionDisabled && "cursor-not-allowed",
                                        )}
                                      >
                                        {doc?.data?.map((docSlot, idx) => {
                                          return docSlot?.prepaid_booking_slot?.toLowerCase() === "yes" ? (
                                            <button
                                              disabled={isSlotSelectionDisabled}
                                              className={clsx(
                                                "border  rounded-[6px]  flex justify-between items-center gap-1 shadow-sm transition-all",
                                                isSlotSelectionDisabled
                                                  ? "cursor-not-allowed"
                                                  : "hover:scale-105 hover:border hover:border-blue-500 hover:bg-blue-100 hover:text-blue-700",
                                                selectedSlotDetails?.slot_id === docSlot.slot_id &&
                                                  "border-blue-500 bg-blue-100 text-blue-700",
                                              )}
                                              style={{
                                                background: "linear-gradient(to top, #81B5FF 0%, #193A6A 100%)",
                                              }}
                                              key={`${docSlot.start_time}:${idx}`}
                                              onClick={
                                                isSlotSelectionDisabled ? null : () => handleSlotSelection(slot, docSlot, doc)
                                              }
                                            >
                                              <div
                                                className={`px-4 py-2  rounded-tl-2xl rounded-tr-md rounded-b-md   ${selectedSlotDetails?.slot_id === docSlot.slot_id ? "bg-blue-50" : "bg-white"}`}
                                              >
                                                {addAMPMToTime(docSlot?.start_time)}{" "}
                                              </div>
                                            </button>
                                          ) : (
                                            <button
                                              disabled={isSlotSelectionDisabled}
                                              className={clsx(
                                                "border  rounded-md px-4 py-2 flex justify-between items-center gap-1 shadow-sm transition-all",
                                                isSlotSelectionDisabled
                                                  ? "cursor-not-allowed"
                                                  : "hover:scale-105 hover:border hover:border-blue-500 hover:bg-blue-100 hover:text-blue-700",
                                                selectedSlotDetails?.slot_id === docSlot.slot_id &&
                                                  "border-blue-500 bg-blue-200 text-blue-700",
                                              )}
                                              key={`${docSlot.start_time}:${idx}`}
                                              onClick={
                                                isSlotSelectionDisabled ? null : () => handleSlotSelection(slot, docSlot, doc)
                                              }
                                            >
                                              {addAMPMToTime(docSlot?.start_time)}{" "}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })}
                              </Disclosure.Panel>
                            )}
                          </>
                        )}
                      </Disclosure>
                    );
                  })}
                  {showPreviousFlowToggle && (
                    <div className="flex items-center gap-2 whitespace-nowrap pt-2">
                      <span className="text-xs font-medium text-[#6C727F]">Show Doctor wise Slots</span>
                      <Switch
                        checked={isPreviousFlow}
                        onChange={handlePreviousFlowChange}
                        className={`${isPreviousFlow ? "bg-[#0E64DD]" : "bg-gray-200"
                          } relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none`}
                      >
                        <span className="sr-only">Show Doctor wise Slots</span>
                        <span
                          aria-hidden="true"
                          className={`${isPreviousFlow ? "translate-x-4" : "translate-x-0"
                            } pointer-events-none h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                        />
                      </Switch>
                    </div>
                  )}
                </div>
                {!isPageReadonly && (
                  <div className="absolute bottom-0 left-0 w-full flex flex-col">
                    <CommentSectionModal buttonName="Next" activeButton={selectedSlotDetails ? true : false} />
                  </div>
                )}
              </div>

              {isHomeServiceSlot && howItWorksPanel}
            </div>
          ) : (
            <div className="bg-white h-screen">
              {atHomeDoctorChips && <div className="p-3">{atHomeDoctorChips}</div>}
              <NoDataFoundComponent
                message="No Slot Found"
                imageURL={BASIC}
                description="No slots available for the given data / filters."
              />
            </div>
          )
        ) : (
          <div className="mt-4 ml-4">
            <SkeletonType1 className="mt-2" />
            <SkeletonType1 className="mt-2" />
          </div>
        )}
      </div>
      {locationShareClinic && (
        <ClinicLocationWhatsapp
          open={!!locationShareClinic}
          setOpen={(isOpen) => {
            if (!isOpen) setLocationShareClinic(null);
          }}
          clinicId={locationShareClinic.clinic_id}
          clinicName={locationShareClinic.clinic_name}
        />
      )}
    </div>
  );
};

export default AvailableAndBookedSlotsComponent;
